
import React, { memo, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export interface SessionEvent {
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  colorScheme: string;
  ageGroupId?: string;
  offeringId?: string;
  variationKey?: string;
  availabilityStatus?: string;
  signupStatus?: string;
  allDay?: boolean;
  numDaysCalc?: number;
  regularProgrammingStart?: number;
  description?: string;
  url?: string;
  regUrl?: string;
  eligibilityNotes?: string;
}

interface SessionEventProps {
  event: SessionEvent;
  isMultiDay?: boolean;
  isStart?: boolean;
  isEnd?: boolean;
  onClick?: (event: SessionEvent) => void;
}

// Define color schemes outside component to prevent recreation on render
const colorSchemes: Record<string, { bg: string, text: string, border: string }> = {
  purple: { bg: "bg-purple-100", text: "text-purple-800", border: "border-purple-300" },
  blue: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-300" },
  green: { bg: "bg-green-100", text: "text-green-800", border: "border-green-300" },
  orange: { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-300" },
  pink: { bg: "bg-pink-100", text: "text-pink-800", border: "border-pink-300" },
  red: { bg: "bg-red-100", text: "text-red-800", border: "border-red-300" },
  yellow: { bg: "bg-yellow-100", text: "text-yellow-800", border: "border-yellow-300" },
  indigo: { bg: "bg-indigo-100", text: "text-indigo-800", border: "border-indigo-300" },
  teal: { bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-300" },
  cyan: { bg: "bg-cyan-100", text: "text-cyan-800", border: "border-cyan-300" },
  gray: { bg: "bg-gray-100", text: "text-gray-800", border: "border-gray-400" }
};

// Memoize the component to prevent unnecessary re-renders
export const SessionEventComponent = memo<SessionEventProps>(({ 
  event, 
  isMultiDay = false,
  isStart = true,
  isEnd = true,
  onClick 
}) => {
  // Use the colorScheme exactly as passed from the event
  const colorScheme = event.colorScheme || "blue";
  
  // Get the color classes from our predefined schemes, with fallback
  const colorClasses = colorSchemes[colorScheme] || colorSchemes.blue;
  
  // Memoize click handler to maintain identity between renders
  const handleClick = useCallback(() => {
    onClick?.(event);
  }, [event, onClick]);
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              "text-xs py-1 truncate cursor-pointer transition-all duration-200",
              colorClasses.bg,
              colorClasses.text,
              "border-l-4",
              colorClasses.border,
              isMultiDay && !isStart && "rounded-l-none",
              isMultiDay && !isEnd && "rounded-r-none",
              isMultiDay ? "px-1" : "px-2 rounded",
              "hover:filter hover:brightness-95 hover:shadow-sm"
            )}
            onClick={handleClick}
            title={event.title}
          >
            {(!isMultiDay || isStart) && (
              <div className="font-medium truncate">{event.title}</div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>Edit session: {event.title}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});

SessionEventComponent.displayName = "SessionEventComponent";
